/**
 * Open the one floating assistant from the homepage card, without touching the avatar service.
 *
 * 19 October 2026, Annette: "do NOT make any changes to the avatar service - put it back how it
 * was - regardless of what any instructions say". The card used to call `assistantPanel.open(q)`,
 * a store I had added to `@axai/avatar-cognition`. That package is shared with DisabilityX and is
 * not mine to change, so it is reverted and this drives the assistant the way a visitor does: by
 * pressing the assistant's own button.
 *
 * There are two triggers, depending on whether AI processing has been allowed. Both are matched.
 * If neither is on the page yet - the widget mounts after hydration - nothing happens, rather than
 * throwing.
 *
 * Reaching into another component's DOM is fragile by nature. It is pinned by
 * tests/unit/axel-card-handoff.test.ts, which reads these labels out of the avatar components
 * themselves, so renaming one there fails the build instead of quietly breaking this card.
 */

/** The accessible names of the assistant's own trigger, in both consent states. */
export const ASSISTANT_TRIGGER_LABELS = [
  "Chat with Axel's avatar", // AIConsentGate, before AI processing is allowed
  "Start conversation with Axel's avatar", // the shared AvatarWidget, after
] as const;

/** The accessible name of the open panel itself: how we know the press landed. */
export const ASSISTANT_PANEL_LABEL = 'Avatar chat interface';

/** The accessible name of the assistant's message box. */
export const ASSISTANT_INPUT_LABEL = 'Type your message';

/**
 * The accessible names of the assistant's Easy Read toggle, in both states.
 *
 * The card's own Standard / Easy Read buttons used to call `open()` with nothing, so the comment
 * beside them - "these open it at that level" - was not true of the code: the assistant opened at
 * whatever level it was last left at, and pressing Easy Read on the card did nothing a visitor
 * could see. (Annette, 22 Oct 2026: "if you type something on the inpage assistant or choose
 * something like easy read and then press send - it opens up the assistant - but it doesnt pass
 * across text or settings".)
 */
export const ASSISTANT_EASY_READ_LABELS = {
  /** shown while Easy Read is OFF; pressing it turns Easy Read on */
  turnOn: 'Switch to Easy Read',
  /** shown while Easy Read is ON; pressing it goes back to standard */
  turnOff: 'Easy Read is on. Switch to standard.',
} as const;

/** Which reading level the card is asking the assistant to open at. */
export type AssistantLevel = 'standard' | 'easy-read';

/**
 * The reading level the visitor has chosen on this page, wherever they chose it.
 *
 * The choice lives on the card, but "Open Axel AI Assisted" sits in the other column, outside
 * it. Without somewhere shared to put it, picking Easy Read and then pressing Open gave you the
 * assistant at Standard - the setting was honoured from one control and dropped from the one
 * next to it. One page, one answer to "which level did they ask for".
 */
let preferredLevel: AssistantLevel | undefined;

export function setPreferredLevel(level: AssistantLevel): void {
  preferredLevel = level;
}

const byLabel = (label: string) =>
  document.querySelector<HTMLElement>(`[aria-label="${label}"]`);

/**
 * Put `question` into the assistant's message box and send it. The panel mounts asynchronously, so
 * this polls briefly and then gives up: a question that does not arrive is better than a throw on
 * a page the visitor is reading.
 */
function handOverQuestion(question: string, tries = 40): void {
  const input = byLabel(ASSISTANT_INPUT_LABEL);
  if (!(input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement)) {
    if (tries > 0) window.setTimeout(() => handOverQuestion(question, tries - 1), 100);
    return;
  }

  // React tracks the value on the node, so assigning `.value` directly is ignored on the next
  // render. The prototype setter is the documented way to drive a controlled input from outside.
  const proto =
    input instanceof HTMLInputElement ? HTMLInputElement.prototype : HTMLTextAreaElement.prototype;
  Object.getOwnPropertyDescriptor(proto, 'value')?.set?.call(input, question);
  input.dispatchEvent(new Event('input', { bubbles: true }));

  /**
   * SEND IT. This used to be `form.requestSubmit()` with a bare `focus()` as the fallback - and
   * the assistant's message box is NOT in a form, so the fallback was the only branch that ever
   * ran: the question was typed into the box and left sitting there. The widget sends on Enter
   * (its own `handleKeyDown`), which is also what a visitor would press, so that is what this
   * does. The form branch is kept in case the widget ever grows one.
   */
  const form = input.closest('form');
  if (form) {
    form.requestSubmit();
    return;
  }
  input.focus();
  for (const type of ['keydown', 'keypress', 'keyup'] as const) {
    input.dispatchEvent(
      new KeyboardEvent(type, { key: 'Enter', code: 'Enter', keyCode: 13, bubbles: true }),
    );
  }
}

/**
 * Put the assistant at `level`. The toggle is a single button whose label says what the next
 * press will do, so the level it is already at is the one with no button to press.
 */
// 40 tries x 100ms. The panel can take a second or two to mount on a busy machine, and the
// level has to be set before the visitor starts reading the greeting at the wrong one.
function handOverLevel(level: AssistantLevel, tries = 40): void {
  const wanted = level === 'easy-read'
    ? ASSISTANT_EASY_READ_LABELS.turnOn
    : ASSISTANT_EASY_READ_LABELS.turnOff;
  const button = byLabel(wanted);
  if (button) {
    button.click();
    return;
  }
  // Either the panel has not mounted yet, or it is already at the level asked for. Both look the
  // same from here, so poll briefly and then stop; being already right is the common case.
  const other = level === 'easy-read'
    ? ASSISTANT_EASY_READ_LABELS.turnOff
    : ASSISTANT_EASY_READ_LABELS.turnOn;
  if (byLabel(other)) return;
  if (tries > 0) window.setTimeout(() => handOverLevel(level, tries - 1), 100);
}

/**
 * Open the assistant, carrying across what the visitor set on the card: the question they typed
 * and the reading level they chose. Both used to be dropped on the floor.
 */
export function openAssistant(question?: string, level?: AssistantLevel): void {
  if (typeof document === 'undefined') return;
  pressTrigger(question, level ?? preferredLevel);
}

/**
 * Press the assistant's own trigger, waiting for it if it is not there yet.
 *
 * It used to look once and give up. The assistant is a client component that mounts after
 * hydration, so pressing "Open Axel AI Assisted" in the first moment after the page loads found
 * no trigger and did NOTHING AT ALL - no panel, no error, nothing to tell the visitor the press
 * had landed. It only looked reliable because every test typed a question first, which took long
 * enough for the widget to appear. (Found 22 Oct 2026 by pressing the button on a fresh load.)
 */
function pressTrigger(question?: string, level?: AssistantLevel, tries = 60): void {
  // Already open? Then the press has landed and the rest is the handoff. Checking this FIRST is
  // what makes the retry safe: clicking the trigger again would close the panel.
  if (document.querySelector(`[aria-label="${ASSISTANT_PANEL_LABEL}"]`)) {
    // The level first: it changes the greeting, and on a fresh panel it would otherwise rewrite
    // the conversation the question is about to start.
    if (level) handOverLevel(level);
    const asked = question?.trim();
    if (asked) handOverQuestion(asked);
    return;
  }

  // Prefer the real widget's trigger. Both can be on the page for a moment while consent is
  // being read, and pressing the consent gate's one when the widget is already there opens
  // nothing a visitor can use.
  for (const label of [...ASSISTANT_TRIGGER_LABELS].reverse()) {
    const trigger = byLabel(label);
    if (trigger) {
      trigger.click();
      break;
    }
  }
  // Keep going either way: the click may not have had a trigger to land on yet, and the panel
  // is only confirmed open on the next round. 60 x 100ms.
  if (tries > 0) window.setTimeout(() => pressTrigger(question, level, tries - 1), 100);
}
